/**
 * Advanced → HTTP connections — the named endpoints a wizard `fetch_url` step
 * reads from: a weighing scale's gateway on the warehouse network, a carrier's
 * tracking API, a device service.
 *
 * The step names the connection (`request: {connection: "scale_gateway",
 * path: "/scales/{header.scale_id}/reading"}`); the base URL and the headers
 * it sends (an API key, a bearer token) live here and never reach a browser.
 * Two things the screen makes obvious:
 *
 *  1. HEADER VALUES ARE WRITE-ONLY. They are stored encrypted and shown masked;
 *     typing a new value replaces the old one, leaving it blank keeps it.
 *  2. A CONNECTION STAYS ON ITS HOST. Requests built from it may only go to
 *     the base URL's host — a path template cannot walk them elsewhere.
 *
 * A downloaded runtime gets the names and base URLs in its bundle; its
 * operator sets the header values in RUNTIME_HTTP_CONNECTIONS.
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Globe, Loader2, Pencil, Plus, Trash2, Zap } from 'lucide-react'
import {
  addHttpConnection, deleteHttpConnection, getHttpConnections, testHttpConnection,
  updateHttpConnection,
  type HttpConnection, type HttpConnectionHeaderInput, type HttpConnectionInput,
  type HttpConnectionProbe,
} from '@/api/httpConnections'

function errText(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail
  return typeof detail === 'string' && detail ? detail : fallback
}

interface HeaderRow extends HttpConnectionHeaderInput {
  /** The masked stored value, for a header that already exists. */
  masked?: string
}

interface Draft {
  id: number | null
  name: string
  description: string
  base_url: string
  enabled: boolean
  headers: HeaderRow[]
}

const BLANK: Draft = { id: null, name: '', description: '', base_url: '', enabled: true, headers: [] }

function draftOf(c: HttpConnection): Draft {
  return {
    id: c.id, name: c.name, description: c.description || '', base_url: c.base_url,
    enabled: c.enabled,
    headers: c.headers.map(h => ({ name: h.name, value: '', masked: h.value_masked })),
  }
}

export default function HttpConnectionsPage() {
  const { project = '' } = useParams<{ project: string }>()
  const navigate = useNavigate()

  const [connections, setConnections] = useState<HttpConnection[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState<Draft | null>(null)
  const [probe, setProbe] = useState<{ id: number; path: string; result?: HttpConnectionProbe } | null>(null)

  const load = useCallback(async () => {
    if (!project) return
    setLoading(true)
    try {
      setConnections((await getHttpConnections(project)).connections || [])
    } catch (err) {
      toast.error(errText(err, 'Could not load this application’s HTTP connections'))
    } finally {
      setLoading(false)
    }
  }, [project])

  useEffect(() => { void load() }, [load])
  useEffect(() => { document.title = `HTTP connections · ${project}` }, [project])

  const run = async (fn: () => Promise<void>, failure: string) => {
    setBusy(true)
    try {
      await fn()
    } catch (err) {
      toast.error(errText(err, failure))
    } finally {
      setBusy(false)
    }
  }

  const onSave = () => run(async () => {
    if (!editing) return
    // Removed headers are sent as removals; a kept one with no new value is
    // sent without one (unchanged); a new one needs a value.
    const headers: HttpConnectionHeaderInput[] = editing.headers
      .filter(h => h.name.trim())
      .map(h => (h.remove ? { name: h.name.trim(), remove: true }
        : { name: h.name.trim(), ...(h.value ? { value: h.value } : {}) }))
    const body: HttpConnectionInput = {
      name: editing.name.trim(), description: editing.description,
      base_url: editing.base_url.trim(), enabled: editing.enabled, headers,
    }
    const res = editing.id == null
      ? await addHttpConnection(project, body)
      : await updateHttpConnection(project, editing.id, body)
    setConnections(res.connections || [])
    setEditing(null)
    toast.success('Saved')
  }, 'Could not save the connection')

  const onDelete = (c: HttpConnection) => run(async () => {
    if (!window.confirm(`Remove connection “${c.name}”?\n\nA wizard step that names it will `
      + 'fail with “Unknown connection”.')) return
    setConnections((await deleteHttpConnection(project, c.id)).connections || [])
  }, 'Could not remove the connection')

  const onProbe = () => run(async () => {
    if (!probe) return
    const result = await testHttpConnection(project, probe.id, probe.path)
    setProbe({ ...probe, result })
  }, 'Could not test the connection')

  const setHeader = (i: number, patch: Partial<HeaderRow>) => setEditing(e => (e ? {
    ...e, headers: e.headers.map((h, j) => (j === i ? { ...h, ...patch } : h)),
  } : e))

  if (loading) {
    return (
      <div style={{ padding: 32, display: 'flex', gap: 10, alignItems: 'center' }}>
        <Loader2 size={18} className="spin" />
        <span style={{ color: 'var(--color-text-secondary)' }}>Loading…</span>
      </div>
    )
  }

  return (
    <div style={{ padding: '0 32px 32px', maxWidth: 1040 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/workbench')}>&larr; Back</button>
        <h1 style={{ fontSize: 23, fontWeight: 600, margin: 0 }}>HTTP connections: {project}</h1>
      </div>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 14, margin: '0 0 20px 44px' }}>
        A wizard&apos;s <em>Read from a URL</em> step names one of these instead of
        carrying an address and a key. The request is made by the server, so the
        base URL and the headers below never reach the person running the wizard.
        Requests built from a connection only ever go to its own host.
      </p>

      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Globe size={16} />
          <div style={{ fontSize: 15, fontWeight: 600, flex: 1 }}>Connections</div>
          <button className="btn btn-primary btn-sm" disabled={busy}
            onClick={() => setEditing({ ...BLANK, headers: [] })}>
            <Plus size={14} /> Add connection
          </button>
        </div>

        {connections.length === 0 && (
          <div style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
            No connections yet.
          </div>
        )}
        {connections.map(c => (
          <div key={c.id} style={{
            padding: '10px 0', borderTop: '1px solid var(--color-border)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 300px', fontSize: 14 }}>
                <div style={{ fontWeight: 500 }}>
                  <code>{c.name}</code>
                  {!c.enabled && <span style={{ color: 'var(--color-warning, #d97706)' }}> · switched off</span>}
                </div>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', wordBreak: 'break-all' }}>
                  {c.base_url}
                  {c.headers.length > 0 && <> · {c.headers.map(h => `${h.name}: ${h.value_masked}`).join(' · ')}</>}
                </div>
                {c.description && (
                  <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{c.description}</div>
                )}
              </div>
              <button className="btn btn-ghost btn-sm" disabled={busy} title="Test"
                onClick={() => setProbe(p => (p?.id === c.id ? null : { id: c.id, path: '' }))}>
                <Zap size={14} /> Test
              </button>
              <button className="btn btn-ghost btn-sm" disabled={busy} title="Edit"
                onClick={() => setEditing(draftOf(c))}>
                <Pencil size={14} />
              </button>
              <button className="btn btn-ghost btn-sm" disabled={busy} title="Remove"
                onClick={() => void onDelete(c)} style={{ color: 'var(--color-error, #dc2626)' }}>
                <Trash2 size={14} />
              </button>
            </div>
            {probe?.id === c.id && (
              <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>GET {c.base_url}</span>
                <input className="form-input" style={{ maxWidth: 280 }} placeholder="/path (optional)"
                  value={probe.path} onChange={(e) => setProbe({ ...probe, path: e.target.value, result: undefined })} />
                <button className="btn btn-sm" disabled={busy} onClick={() => void onProbe()}>Send</button>
                {probe.result && (
                  <div style={{
                    flexBasis: '100%', fontSize: 13,
                    color: probe.result.ok ? 'var(--color-success, #16a34a)' : 'var(--color-error, #dc2626)',
                  }}>
                    {probe.result.ok ? `HTTP ${probe.result.status} — reachable` : (probe.result.error || 'Failed')}
                    {probe.result.preview && (
                      <pre style={{
                        margin: '6px 0 0', padding: 8, fontSize: 13, whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all', background: 'var(--color-bg-secondary, rgba(0,0,0,0.05))',
                        color: 'var(--color-text)', borderRadius: 6, maxHeight: 160, overflow: 'auto',
                      }}>{probe.result.preview}</pre>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <div className="card" style={{ padding: 20, borderLeft: '3px solid var(--color-accent)' }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>
            {editing.id == null ? 'New connection' : `Edit “${editing.name}”`}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(140px, 180px) 1fr', gap: '10px 12px', alignItems: 'center' }}>
            <label style={{ fontSize: 14 }}>Name</label>
            <input className="form-input" placeholder="scale_gateway" value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            <label style={{ fontSize: 14 }}>Base URL</label>
            <input className="form-input" placeholder="http://10.0.4.20:8080/api" value={editing.base_url}
              onChange={(e) => setEditing({ ...editing, base_url: e.target.value })} />
            <label style={{ fontSize: 14 }}>Description</label>
            <input className="form-input" placeholder="Optional" value={editing.description}
              onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            <label style={{ fontSize: 14 }}>Enabled</label>
            <label style={{ fontSize: 14, display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={editing.enabled}
                onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} />
              Steps may use this connection
            </label>
          </div>

          <div style={{ fontSize: 14, fontWeight: 600, margin: '16px 0 4px' }}>Headers</div>
          <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '0 0 8px' }}>
            Sent with every request — typically <code>Authorization</code> or an API-key
            header. Values are stored encrypted and never shown again; leave a value
            blank to keep the stored one.
          </p>
          {editing.headers.map((h, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, opacity: h.remove ? 0.5 : 1 }}>
              <input className="form-input" style={{ maxWidth: 220 }} placeholder="Header name"
                value={h.name} disabled={!!h.masked}
                onChange={(e) => setHeader(i, { name: e.target.value })} />
              <input className="form-input" type="password" autoComplete="new-password"
                placeholder={h.masked ? `${h.masked} (unchanged)` : 'Value'}
                value={h.value || ''} disabled={h.remove}
                onChange={(e) => setHeader(i, { value: e.target.value })} />
              <button className="btn btn-ghost btn-sm" title={h.remove ? 'Keep' : 'Remove'}
                onClick={() => (h.masked
                  ? setHeader(i, { remove: !h.remove })
                  : setEditing({ ...editing, headers: editing.headers.filter((_, j) => j !== i) }))}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <button className="btn btn-ghost btn-sm" onClick={() => setEditing({
            ...editing, headers: [...editing.headers, { name: '', value: '' }],
          })}>
            <Plus size={14} /> Add header
          </button>

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => void onSave()}>Save</button>
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  )
}
